/**
 * 가이드 시스템의 localStorage 키 단일 진실 공급원 (Single Source of Truth).
 *
 * 네임스페이스: `wkwk:guide:*` — 콜론 구분으로 통일 (기존 kebab/snake 혼재 해소).
 *
 * 마이그레이션: `migrateGuideStorageKeys()` — GuideProvider 마운트 시 1회 실행하여
 * 구 prefix(`guide-dismissed-`, `output_coachmark_` 등) 데이터를 신 키로 이전.
 * `migration-version` 키로 idempotent 보장.
 */

const ROOT = 'wkwk:guide';

export const GUIDE_STORAGE_KEYS = {
  /** 투어 가이드 영구 dismiss — key: guide key (home, lts, ...) */
  dismissed: (key: string) => `${ROOT}:dismissed:${key}`,
  /** 세션 최초 1회 자동 노출 여부 */
  autoShown: (key: string) => `${ROOT}:auto-shown:${key}`,
  /** Output 코치마크 dismiss — key: coachmark key */
  outputCoachmark: (key: string) => `${ROOT}:coachmark:output:${key}`,
  /** STC 코치마크 dismiss */
  stcCoachmark: (key: string) => `${ROOT}:coachmark:stc:${key}`,
  /** Idle 힌트 완료 상태 — storageKey: guideType × hasStories 조합 */
  idleHints: (storageKey: string) => `${ROOT}:idle-hints:${storageKey}`,
  /** 가이드 기능 마스터 토글 (debug) */
  debugEnabled: `${ROOT}:debug-enabled`,
  /** 디버그: idle hint auto-dismiss 시간 override */
  debugIdleAutoDismiss: `${ROOT}:debug-idle-hint:auto-dismiss`,
  /** 디버그: idle hint trigger 지연 override */
  debugIdleTriggerDelay: `${ROOT}:debug-idle-hint:trigger-delay`,
  /** 디버그: idle hint next interval override */
  debugIdleNextInterval: `${ROOT}:debug-idle-hint:next-interval`,
  /** 마이그레이션 버전 (idempotency) */
  migrationVersion: `${ROOT}:migration-version`,
} as const;

/** prefix 매칭용 상수 — startsWith 스캔 시 사용 */
export const GUIDE_STORAGE_PREFIXES = {
  dismissed: `${ROOT}:dismissed:`,
  autoShown: `${ROOT}:auto-shown:`,
  idleHints: `${ROOT}:idle-hints:`,
  outputCoachmark: `${ROOT}:coachmark:output:`,
  stcCoachmark: `${ROOT}:coachmark:stc:`,
} as const;

const CURRENT_MIGRATION_VERSION = 1;

/** 구→신 prefix 매핑 (migration v1) */
const LEGACY_PREFIX_MAP: Array<{
  old: string;
  toNew: (rest: string) => string;
}> = [
  {
    old: 'guide-dismissed-',
    toNew: (rest) => GUIDE_STORAGE_KEYS.dismissed(rest),
  },
  {
    old: 'guide-auto-shown-',
    toNew: (rest) => GUIDE_STORAGE_KEYS.autoShown(rest),
  },
  {
    old: 'output_coachmark_',
    toNew: (rest) => GUIDE_STORAGE_KEYS.outputCoachmark(rest),
  },
  {
    old: 'stc_coachmark_',
    toNew: (rest) => GUIDE_STORAGE_KEYS.stcCoachmark(rest),
  },
  { old: 'idle_hints_', toNew: (rest) => GUIDE_STORAGE_KEYS.idleHints(rest) },
];

const LEGACY_EXACT_MAP: Record<string, string> = {
  'debug-guide-enabled': GUIDE_STORAGE_KEYS.debugEnabled,
  'debug-idle-hint-auto-dismiss': GUIDE_STORAGE_KEYS.debugIdleAutoDismiss,
  'debug-idle-hint-trigger-delay': GUIDE_STORAGE_KEYS.debugIdleTriggerDelay,
  'debug-idle-hint-next-interval': GUIDE_STORAGE_KEYS.debugIdleNextInterval,
};

/**
 * 구 localStorage 키를 신 네임스페이스로 이전.
 * idempotent — 여러 번 호출해도 안전.
 */
export function migrateGuideStorageKeys(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(GUIDE_STORAGE_KEYS.migrationVersion);
    const version = raw ? parseInt(raw, 10) : 0;
    if (version >= CURRENT_MIGRATION_VERSION) return;

    const toRename: Array<[oldKey: string, newKey: string, value: string]> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      const exactNew = LEGACY_EXACT_MAP[key];
      if (exactNew) {
        const value = localStorage.getItem(key);
        if (value !== null) toRename.push([key, exactNew, value]);
        continue;
      }

      for (const { old, toNew } of LEGACY_PREFIX_MAP) {
        if (key.startsWith(old)) {
          const rest = key.slice(old.length);
          const value = localStorage.getItem(key);
          if (value !== null) toRename.push([key, toNew(rest), value]);
          break;
        }
      }
    }

    for (const [oldKey, newKey, value] of toRename) {
      // 이미 신 키가 존재하면 신 키를 우선 유지 (동시 migration 안전성)
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
      }
      localStorage.removeItem(oldKey);
    }

    localStorage.setItem(
      GUIDE_STORAGE_KEYS.migrationVersion,
      String(CURRENT_MIGRATION_VERSION),
    );
  } catch {
    // localStorage 사용 불가 시 무시 — 다음 마운트에서 재시도
  }
}
